import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import Topbar from '../components/Topbar';
import api from '../api';

const COLORS = ['#066ac9','#0cbc87','#f7c32e','#d6293e','#6f42c1','#fd7e14','#17a2b8','#e83e8c','#20c997','#6610f2'];
const WC_COLORS = ['#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#9B59B6','#fd7e14','#0cbc87','#066ac9','#e83e8c','#f7c32e'];

const SIGNALEMENT_TYPES = [
  { key: 'comportement', icon: 'bi-exclamation-triangle', color: '#fd7e14', label: 'Comportement' },
  { key: 'famille',      icon: 'bi-house-door',           color: '#6f42c1', label: 'Famille' },
  { key: 'sante',        icon: 'bi-heart',                color: '#0cbc87', label: 'Santé' },
  { key: 'scolaire',     icon: 'bi-book',                 color: '#066ac9', label: 'Scolaire' },
  { key: 'autre',        icon: 'bi-flag',                 color: '#6c757d', label: 'Autre' },
];

function initiales(prenom, nom) {
  return `${(prenom||'?')[0]}${(nom||'?')[0]}`.toUpperCase();
}

// ── Nuage de mots ────────────────────────────────────────────────────────────
function WordCloud({ reponses }) {
  const tags = useMemo(() => {
    const counts = {};
    reponses.forEach(r => {
      if (!r) return;
      const parts = r.split(/[,;]/).map(p => p.trim()).filter(p => p.length > 1);
      const items = parts.length > 1 ? parts : [r.trim()];
      items.forEach(item => {
        const key = item.toLowerCase();
        if (!counts[key]) counts[key] = { text: item, count: 0 };
        counts[key].count++;
      });
    });
    const sorted = Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 40);
    return [...sorted].sort(() => Math.random() - 0.5);
  }, [reponses]);

  if (tags.length === 0) return <p className="text-muted text-center py-3">Aucune réponse</p>;

  const maxCount = Math.max(...tags.map(t => t.count), 1);

  return (
    <div style={{ textAlign: 'center', padding: '8px 4px', lineHeight: 2 }}>
      {tags.map((tag, i) => {
        const ratio = tag.count / maxCount;
        const fontSize = Math.round(12 + ratio * 16);
        const rotation = (((i * 137) % 13) - 6); // pseudo-random but stable
        return (
          <span
            key={tag.text}
            style={{
              display: 'inline-block',
              padding: '5px 13px',
              margin: '4px',
              borderRadius: 20,
              fontWeight: 500,
              fontSize,
              background: WC_COLORS[i % WC_COLORS.length],
              color: 'white',
              transform: `rotate(${rotation}deg)`,
              cursor: 'default',
              userSelect: 'none',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = `rotate(${rotation}deg) translateY(-2px)`; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = `rotate(${rotation}deg)`; e.currentTarget.style.boxShadow = ''; }}
          >
            {tag.text}
            {tag.count > 1 && (
              <span style={{ background: 'rgba(255,255,255,0.25)', padding: '1px 5px', borderRadius: 10, fontSize: '0.75em', marginLeft: 5 }}>
                {tag.count}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

// ── Accordéon texte long ─────────────────────────────────────────────────────
function TextAccordion({ reponses }) {
  const [openIdx, setOpenIdx] = useState(null);

  if (reponses.length === 0) return <p className="text-muted text-center py-3">Aucune réponse</p>;

  return (
    <div>
      {reponses.map((r, i) => {
        const isOpen = openIdx === i;
        const preview = r.length > 90 ? r.slice(0, 90) + '…' : r;
        return (
          <div
            key={i}
            onClick={() => setOpenIdx(isOpen ? null : i)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px 4px',
              borderBottom: i < reponses.length - 1 ? '1px solid #f0f2f5' : 'none',
              cursor: 'pointer',
            }}
          >
            <i
              className={`bi ${isOpen ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted`}
              style={{ fontSize: '0.7rem', marginTop: 4, flexShrink: 0 }}
            ></i>
            <span style={{
              fontSize: '0.875rem',
              color: isOpen ? '#212529' : '#6c757d',
              overflow: 'hidden',
              textOverflow: isOpen ? 'unset' : 'ellipsis',
              whiteSpace: isOpen ? 'pre-wrap' : 'nowrap',
              flex: 1,
            }}>
              {isOpen ? r : preview}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Camembert d'âges ─────────────────────────────────────────────────────────
function AgeChart({ reponses }) {
  const { data } = useMemo(() => {
    const today = new Date();
    const ageCounts = {};
    reponses.forEach(dateStr => {
      if (!dateStr) return;
      const birth = new Date(dateStr);
      if (isNaN(birth.getTime())) return;
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      if (age > 0 && age < 100) ageCounts[age] = (ageCounts[age] || 0) + 1;
    });
    const data = Object.entries(ageCounts)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([age, count]) => ({ name: `${age} ans`, value: count }));
    return { data };
  }, [reponses]);

  if (data.length === 0) return <p className="text-muted text-center py-3">Aucune date valide</p>;

  return (
    <>
      <ResponsiveContainer width="100%" height={270}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            outerRadius={95}
            dataKey="value"
            label={({ name, percent }) => `${name} · ${(percent * 100).toFixed(0)}%`}
            labelLine
          >
            {data.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${v} élève${v > 1 ? 's' : ''}`, n]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="d-flex flex-wrap gap-2 mt-1 justify-content-center">
        {data.map((d, idx) => (
          <span key={idx} className="d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[idx % COLORS.length], display: 'inline-block', flexShrink: 0 }}></span>
            {d.name} <span className="text-muted">({d.value})</span>
          </span>
        ))}
      </div>
    </>
  );
}

// ── Vue Élèves ──────────────────────────────────────────────────────────────
function VueEleves({ eleves }) {
  const [search, setSearch] = useState('');
  const filtered = eleves.filter(e =>
    `${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-4">
        <div style={{ flex: 1 }}>
          <div className="input-group">
            <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
            <input
              className="form-control border-start-0"
              placeholder="Rechercher un élève..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
        <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
          {filtered.length} élève{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5 text-muted">
            <i className="bi bi-person-x" style={{ fontSize: '2.5rem', opacity: 0.2 }}></i>
            <p className="mt-2">Aucun élève dans cette classe.</p>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {filtered.map((e, i) => (
            <div key={e.id} className="col-sm-6 col-xl-4">
              <Link to={`/eleve/${e.id}`} className="text-decoration-none">
                <div className="card h-100"
                  style={{ transition: 'transform 0.15s, box-shadow 0.15s', cursor: 'pointer' }}
                  onMouseEnter={ev => { ev.currentTarget.style.transform = 'translateY(-2px)'; ev.currentTarget.style.boxShadow = '0 6px 20px rgba(6,106,201,0.12)'; }}
                  onMouseLeave={ev => { ev.currentTarget.style.transform = ''; ev.currentTarget.style.boxShadow = ''; }}
                >
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="avatar-sm" style={{ background: COLORS[i % COLORS.length], width: 46, height: 46, fontSize: '1rem', borderRadius: 12, flexShrink: 0 }}>
                      {initiales(e.prenom, e.nom)}
                    </div>
                    <div className="min-w-0">
                      <div className="fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>{e.prenom} {e.nom}</div>
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>{e.formulaire_titre || '—'}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        <i className="bi bi-chat-square-text me-1"></i>{e.nb_reponses} réponse{e.nb_reponses !== 1 ? 's' : ''}
                      </div>
                      {e.signalements && e.signalements.length > 0 && (
                        <div className="d-flex gap-1 mt-1 flex-wrap">
                          {SIGNALEMENT_TYPES.filter(s => e.signalements.includes(s.key)).map(s => (
                            <span key={s.key} title={s.label} style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 18, height: 18, borderRadius: 4,
                              background: s.color, color: '#fff', fontSize: '0.6rem',
                            }}>
                              <i className={`bi ${s.icon}`}></i>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <i className="bi bi-chevron-right text-muted ms-auto"></i>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Vue Globale ──────────────────────────────────────────────────────────────
function VueGlobale({ classeId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(new Set());

  const toggleCollapsed = (id) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  useEffect(() => {
    api.get(`/classes/${classeId}/stats`).then(r => {
      setStats(r.data);
      setCollapsed(new Set(r.data.questions.filter(q => q.type === 'texte_long').map(q => q.id)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [classeId]);

  if (loading) return (
    <div className="d-flex justify-content-center py-5">
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
    </div>
  );

  if (!stats || !stats.questions?.length) return (
    <div className="card">
      <div className="card-body text-center py-5 text-muted">
        <i className="bi bi-bar-chart-line" style={{ fontSize: '2.5rem', opacity: 0.2 }}></i>
        <p className="mt-2">Aucune donnée disponible. Les élèves n'ont pas encore répondu à un formulaire.</p>
      </div>
    </div>
  );

  const firstStats = stats.questions.find(q => q.stats);
  const tauxParticipation = firstStats && stats.nb_eleves > 0
    ? Math.round(firstStats.stats.nb_repondants / stats.nb_eleves * 100)
    : 0;

  return (
    <>
      {/* Résumé */}
      <div className="row g-3 mb-4">
        <div className="col-sm-4">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e8f0fe' }}>
              <i className="bi bi-people-fill" style={{ color: '#066ac9' }}></i>
            </div>
            <div>
              <div className="stat-value">{stats.nb_eleves}</div>
              <div className="stat-label">Élèves</div>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#cef2e7' }}>
              <i className="bi bi-clipboard-check-fill" style={{ color: '#0cbc87' }}></i>
            </div>
            <div>
              <div className="stat-value">{stats.questions.filter(q => q.type !== 'separateur').length}</div>
              <div className="stat-label">Questions</div>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fdf3d5' }}>
              <i className="bi bi-percent" style={{ color: '#f7c32e' }}></i>
            </div>
            <div>
              <div className="stat-value">{tauxParticipation}%</div>
              <div className="stat-label">Participation</div>
            </div>
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="row g-4">
        {stats.questions.map(q => {
          if (q.type === 'separateur') {
            return (
              <div key={q.id} className="col-12">
                <h5 className="fw-semibold text-primary border-bottom pb-2 mb-0">{q.libelle}</h5>
              </div>
            );
          }

          const s = q.stats;
          if (!s) return null;

          const pctReponse = s.nb_eleves > 0 ? Math.round(s.nb_repondants / s.nb_eleves * 100) : 0;
          const isWideCard = s.type === 'chart' || (s.type === 'texte' && (q.type === 'texte_court' || (q.type === 'date' && /naissance/i.test(q.libelle))));

          const isLong = s.type === 'texte' && q.type === 'texte_long';
          const isCollapsed = collapsed.has(q.id);

          return (
            <div key={q.id} className={isWideCard ? 'col-12 col-xl-6' : 'col-12'}>
              <div className="card h-100">
                <div
                  className="card-header"
                  onClick={isLong ? () => toggleCollapsed(q.id) : undefined}
                  style={isLong ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                >
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{q.libelle}</div>
                    {isLong && (
                      <i className={`bi ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'} text-muted`} style={{ fontSize: '0.8rem', flexShrink: 0 }}></i>
                    )}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {s.nb_repondants} / {s.nb_eleves} réponse{s.nb_repondants !== 1 ? 's' : ''} · {pctReponse}% de participation
                  </div>
                </div>
                {(!isLong || !isCollapsed) && <div className="card-body">

                  {/* Diagramme circulaire */}
                  {s.type === 'chart' && (
                    s.data.length === 0 ? (
                      <p className="text-muted text-center py-3">Aucune réponse</p>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={270}>
                          <PieChart>
                            <Pie
                              data={s.data}
                              cx="50%"
                              cy="50%"
                              outerRadius={95}
                              dataKey="value"
                              label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                              labelLine
                            >
                              {s.data.map((_, idx) => (
                                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [`${value} élève${value > 1 ? 's' : ''}`, name]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="d-flex flex-wrap gap-2 mt-1">
                          {s.data.map((d, idx) => (
                            <span key={idx} className="d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                              <span style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[idx % COLORS.length], display: 'inline-block', flexShrink: 0 }}></span>
                              {d.name} <span className="text-muted">({d.value})</span>
                            </span>
                          ))}
                        </div>
                      </>
                    )
                  )}

                  {/* Note / étoiles */}
                  {s.type === 'note' && (
                    <>
                      <div className="text-center mb-3">
                        <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#f7c32e', lineHeight: 1 }}>{s.moyenne ?? '—'}</div>
                        <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>moyenne / 5</div>
                        <div>
                          {[1,2,3,4,5].map(n => (
                            <i key={n} className={`bi bi-star${parseFloat(s.moyenne) >= n ? '-fill' : ''}`}
                              style={{ color: '#f7c32e', fontSize: '1.3rem' }}></i>
                          ))}
                        </div>
                      </div>
                      {s.distribution.map(d => (
                        <div key={d.note} className="d-flex align-items-center gap-2 mb-1">
                          <span style={{ width: 28, fontSize: '0.8rem', textAlign: 'right', flexShrink: 0 }}>{d.note}★</span>
                          <div className="flex-grow-1 rounded" style={{ height: 10, background: '#f0f2f5', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: s.nb_repondants ? `${d.count / s.nb_repondants * 100}%` : '0%',
                              background: '#f7c32e',
                              borderRadius: 4,
                              transition: 'width 0.5s',
                            }} />
                          </div>
                          <span style={{ width: 24, fontSize: '0.8rem', flexShrink: 0 }}>{d.count}</span>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Texte court → nuage de mots */}
                  {s.type === 'texte' && q.type === 'texte_court' && (
                    <WordCloud reponses={s.reponses} />
                  )}

                  {/* Texte long → liste simple (la card est en accordéon) */}
                  {s.type === 'texte' && q.type === 'texte_long' && (
                    s.reponses.length === 0
                      ? <p className="text-muted text-center py-3">Aucune réponse</p>
                      : <div>
                          {s.reponses.map((r, ri) => (
                            <div key={ri} style={{ fontSize: '0.875rem', padding: '8px 0', borderBottom: ri < s.reponses.length - 1 ? '1px solid #f0f2f5' : 'none' }}>
                              {r}
                            </div>
                          ))}
                        </div>
                  )}

                  {/* Date de naissance → répartition des âges */}
                  {s.type === 'texte' && q.type === 'date' && /naissance/i.test(q.libelle) && (
                    <AgeChart reponses={s.reponses} />
                  )}

                  {/* Autres dates / nombres → liste */}
                  {s.type === 'texte' && !(q.type === 'texte_court') && !(q.type === 'texte_long') && !(q.type === 'date' && /naissance/i.test(q.libelle)) && (
                    s.reponses.length === 0
                      ? <p className="text-muted text-center py-3">Aucune réponse</p>
                      : <ul className="list-group list-group-flush">
                          {s.reponses.map((r, ri) => (
                            <li key={ri} className="list-group-item px-0 py-1" style={{ fontSize: '0.875rem' }}>
                              <i className="bi bi-chat-right-text text-muted me-2" style={{ fontSize: '0.75rem' }}></i>
                              {r}
                            </li>
                          ))}
                        </ul>
                  )}

                </div>}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Page principale ──────────────────────────────────────────────────────────
export default function ClasseDetail() {
  const { id } = useParams();
  const [classe, setClasse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onglet, setOnglet] = useState('eleves');

  useEffect(() => {
    api.get(`/classes/${id}/eleves`).then(r => {
      setClasse(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ height: '80vh' }}>
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
    </div>
  );
  if (!classe) return <div className="page-content"><div className="alert alert-danger">Classe introuvable</div></div>;

  return (
    <>
      <Topbar
        title={classe.nom}
        actions={
          <Link to="/classes" className="btn btn-light">
            <i className="bi bi-arrow-left me-2"></i>Retour
          </Link>
        }
      />
      <div className="page-content">

        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${onglet === 'eleves' ? 'active' : ''}`}
              onClick={() => setOnglet('eleves')}
            >
              <i className="bi bi-people me-2"></i>Élèves
              <span className="badge ms-2" style={{ background: onglet === 'eleves' ? 'rgba(255,255,255,0.3)' : '#e8f0fe', color: onglet === 'eleves' ? '#fff' : '#066ac9', fontSize: '0.75rem' }}>
                {classe.eleves?.length || 0}
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${onglet === 'global' ? 'active' : ''}`}
              onClick={() => setOnglet('global')}
            >
              <i className="bi bi-bar-chart-pie me-2"></i>Vue globale
            </button>
          </li>
        </ul>

        {onglet === 'eleves' && <VueEleves eleves={classe.eleves || []} />}
        {onglet === 'global' && <VueGlobale classeId={id} />}

      </div>
    </>
  );
}
